'use client';
import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MapContainer, TileLayer, Marker, useMapEvents, useMap } from 'react-leaflet';
import L from 'leaflet';
import { Camera, X, MapPin, Send, ImagePlus, Navigation, AlertTriangle } from 'lucide-react';
import 'leaflet/dist/leaflet.css';

// Custom red marker for flood reports
const floodReportIcon = new L.Icon({
    iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-red.png',
    shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
    iconSize: [25, 41],
    iconAnchor: [12, 41],
    popupAnchor: [1, -34],
    shadowSize: [41, 41]
});

// Component to handle map clicks and marker dragging
function LocationPicker({ position, setPosition }) {
    const map = useMapEvents({
        click(e) {
            setPosition([e.latlng.lat, e.latlng.lng]);
        }
    });

    return position ? (
        <Marker
            position={position}
            icon={floodReportIcon}
            draggable={true}
            eventHandlers={{
                dragend: (e) => {
                    const marker = e.target;
                    const pos = marker.getLatLng();
                    setPosition([pos.lat, pos.lng]);
                }
            }}
        />
    ) : null;
}

// Component to recenter map
function RecenterButton({ onRecenter }) {
    return (
        <button
            onClick={onRecenter}
            style={{
                position: 'absolute',
                bottom: 10,
                right: 10,
                zIndex: 1000,
                background: 'rgba(15, 23, 42, 0.9)',
                border: '1px solid #334155',
                borderRadius: 8,
                padding: '8px 12px',
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                color: '#60a5fa',
                cursor: 'pointer',
                fontSize: '0.8rem'
            }}
        >
            <Navigation size={16} /> Mi ubicación
        </button>
    );
}

// Main Modal Component
export default function CitizenReportModal({ isOpen, onClose, onSubmit, initialGps }) {
    const [photo, setPhoto] = useState(null);
    const [photoPreview, setPhotoPreview] = useState(null);
    const [position, setPosition] = useState(null);
    const [description, setDescription] = useState('');
    const [waterLevelEstimate, setWaterLevelEstimate] = useState('medio');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState(null);
    const fileInputRef = useRef(null);
    const cameraInputRef = useRef(null);
    const mapRef = useRef(null);

    // Default to Santo Domingo
    const defaultCenter = [18.4861, -69.9312];

    // Set initial position from GPS or get user's location
    useEffect(() => {
        if (isOpen) {
            if (initialGps) {
                const [lat, lng] = initialGps.split(',').map(Number);
                if (!isNaN(lat) && !isNaN(lng)) {
                    setPosition([lat, lng]);
                    return;
                }
            }
            // Try to get user's location
            if (navigator.geolocation) {
                navigator.geolocation.getCurrentPosition(
                    (pos) => {
                        setPosition([pos.coords.latitude, pos.coords.longitude]);
                    },
                    () => {
                        // Use default if geolocation fails
                        setPosition(defaultCenter);
                    }
                );
            } else {
                setPosition(defaultCenter);
            }
        }
    }, [isOpen, initialGps]);

    // Handle file selection
    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            if (file.size > 10 * 1024 * 1024) { // 10MB limit
                setError('La imagen es muy grande. Máximo 10MB.');
                return;
            }
            setPhoto(file);
            const reader = new FileReader();
            reader.onloadend = () => {
                setPhotoPreview(reader.result);
            };
            reader.readAsDataURL(file);
            setError(null);
        }
    };

    // Handle camera capture using native API as fallback
    const handleCameraCapture = async () => {
        // First try the input element (works best on mobile)
        if (cameraInputRef.current) {
            cameraInputRef.current.click();
            return;
        }

        // Fallback: Use MediaDevices API for browsers that support it
        try {
            if (navigator.mediaDevices && navigator.mediaDevices.getUserMedia) {
                const stream = await navigator.mediaDevices.getUserMedia({
                    video: { facingMode: 'environment' }
                });

                // Create video element to capture frame
                const video = document.createElement('video');
                video.srcObject = stream;
                video.play();

                // Wait for video to be ready
                await new Promise(resolve => {
                    video.onloadedmetadata = resolve;
                });

                // Create canvas and capture frame
                const canvas = document.createElement('canvas');
                canvas.width = video.videoWidth;
                canvas.height = video.videoHeight;
                const ctx = canvas.getContext('2d');
                ctx.drawImage(video, 0, 0);

                // Stop stream
                stream.getTracks().forEach(track => track.stop());

                // Convert to base64
                const dataUrl = canvas.toDataURL('image/jpeg', 0.8);
                setPhotoPreview(dataUrl);
                setPhoto({ name: 'camera_photo.jpg', type: 'image/jpeg' });
                setError(null);
            }
        } catch (err) {
            console.error('Camera access error:', err);
            setError('No se pudo acceder a la cámara. Intenta seleccionar una foto de la galería.');
        }
    };

    // Handle recenter to user location
    const handleRecenter = () => {
        if (navigator.geolocation) {
            navigator.geolocation.getCurrentPosition(
                (pos) => {
                    const newPos = [pos.coords.latitude, pos.coords.longitude];
                    setPosition(newPos);
                    if (mapRef.current) {
                        mapRef.current.setView(newPos, 16);
                    }
                },
                () => {
                    setError('No se pudo obtener tu ubicación.');
                }
            );
        }
    };

    // Handle form submission
    const handleSubmit = async () => {
        if (!photo) {
            setError('Por favor, selecciona una foto de la inundación.');
            return;
        }
        if (!position) {
            setError('Por favor, marca la ubicación de la inundación en el mapa.');
            return;
        }

        setIsSubmitting(true);
        setError(null);

        try {
            const reportData = {
                photo: photoPreview, // Base64 for now
                location: {
                    lat: position[0],
                    lng: position[1]
                },
                description: description || 'Reporte ciudadano de inundación',
                waterLevelEstimate,
                timestamp: new Date().toISOString()
            };

            if (onSubmit) {
                await onSubmit(reportData);
            }

            // Reset form
            setPhoto(null);
            setPhotoPreview(null);
            setDescription('');
            setWaterLevelEstimate('medio');
            onClose();

            alert('✅ ¡Reporte enviado exitosamente! Gracias por tu colaboración.');
        } catch (err) {
            console.error('Error submitting report:', err);
            setError('Error al enviar el reporte. Intenta nuevamente.');
        } finally {
            setIsSubmitting(false);
        }
    };

    // Reset state when modal closes
    useEffect(() => {
        if (!isOpen) {
            setPhoto(null);
            setPhotoPreview(null);
            setDescription('');
            setWaterLevelEstimate('medio');
            setError(null);
            setPosition(null);
        }
    }, [isOpen]);

    if (!isOpen) return null;

    return (
        <AnimatePresence>
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: 'rgba(0, 0, 0, 0.9)',
                    backdropFilter: 'blur(8px)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 300,
                    padding: 16
                }}
            >
                <motion.div
                    initial={{ scale: 0.9, opacity: 0, y: 20 }}
                    animate={{ scale: 1, opacity: 1, y: 0 }}
                    exit={{ scale: 0.9, opacity: 0, y: 20 }}
                    style={{
                        width: '100%',
                        maxWidth: 500,
                        maxHeight: '90vh',
                        overflowY: 'auto',
                        background: 'linear-gradient(145deg, rgba(15, 23, 42, 0.98), rgba(30, 41, 59, 0.98))',
                        borderRadius: 20,
                        border: '1px solid rgba(59, 130, 246, 0.3)',
                        boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5), 0 0 30px rgba(59, 130, 246, 0.15)'
                    }}
                >
                    {/* Header */}
                    <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: '20px 20px 0 20px',
                        borderBottom: '1px solid #334155',
                        paddingBottom: 15
                    }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                            <div style={{
                                background: 'linear-gradient(135deg, #f59e0b, #d97706)',
                                borderRadius: 12,
                                padding: 10,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}>
                                <Camera size={24} color="white" />
                            </div>
                            <div>
                                <h3 style={{ margin: 0, color: '#fff', fontSize: '1.2rem' }}>Reporte Ciudadano</h3>
                                <p style={{ margin: 0, color: '#94a3b8', fontSize: '0.85rem' }}>Reporta una inundación</p>
                            </div>
                        </div>
                        <button
                            onClick={onClose}
                            style={{
                                background: 'rgba(255,255,255,0.1)',
                                border: 'none',
                                borderRadius: 8,
                                padding: 8,
                                cursor: 'pointer',
                                color: '#94a3b8'
                            }}
                        >
                            <X size={24} />
                        </button>
                    </div>

                    {/* Content */}
                    <div style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 20 }}>

                        {/* Error Message */}
                        {error && (
                            <div style={{
                                background: 'rgba(239, 68, 68, 0.15)',
                                border: '1px solid #ef4444',
                                borderRadius: 10,
                                padding: 12,
                                display: 'flex',
                                alignItems: 'center',
                                gap: 10,
                                color: '#fca5a5'
                            }}>
                                <AlertTriangle size={18} />
                                <span style={{ fontSize: '0.9rem' }}>{error}</span>
                            </div>
                        )}

                        {/* Photo Upload Section */}
                        <div>
                            <label style={{ display: 'block', marginBottom: 8, color: '#cbd5e1', fontSize: '0.9rem', fontWeight: 500 }}>
                                📷 Foto de la Inundación *
                            </label>

                            {/* Hidden inputs - one for camera, one for gallery */}
                            <input
                                type="file"
                                accept="image/jpeg,image/png,image/webp,image/*"
                                capture
                                ref={cameraInputRef}
                                onChange={handleFileChange}
                                style={{ display: 'none' }}
                                id="camera-input"
                            />
                            <input
                                type="file"
                                accept="image/jpeg,image/png,image/webp,image/*"
                                ref={fileInputRef}
                                onChange={handleFileChange}
                                style={{ display: 'none' }}
                                id="gallery-input"
                            />

                            {photoPreview ? (
                                <div style={{ position: 'relative' }}>
                                    <img
                                        src={photoPreview}
                                        alt="Preview"
                                        style={{
                                            width: '100%',
                                            height: 180,
                                            objectFit: 'cover',
                                            borderRadius: 12,
                                            border: '2px solid #334155'
                                        }}
                                    />
                                    <button
                                        onClick={() => {
                                            setPhoto(null);
                                            setPhotoPreview(null);
                                        }}
                                        style={{
                                            position: 'absolute',
                                            top: 8,
                                            right: 8,
                                            background: 'rgba(239, 68, 68, 0.9)',
                                            border: 'none',
                                            borderRadius: 6,
                                            padding: 6,
                                            cursor: 'pointer',
                                            color: 'white'
                                        }}
                                    >
                                        <X size={16} />
                                    </button>
                                </div>
                            ) : (
                                <div style={{ display: 'flex', gap: 12 }}>
                                    {/* Camera Button */}
                                    <button
                                        onClick={handleCameraCapture}
                                        type="button"
                                        style={{
                                            flex: 1,
                                            height: 100,
                                            background: 'linear-gradient(135deg, rgba(245, 158, 11, 0.15), rgba(217, 119, 6, 0.1))',
                                            border: '2px solid #f59e0b',
                                            borderRadius: 12,
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            gap: 8,
                                            cursor: 'pointer',
                                            color: '#f59e0b',
                                            transition: 'all 0.2s'
                                        }}
                                    >
                                        <Camera size={28} />
                                        <span style={{ fontSize: '0.85rem', fontWeight: 500 }}>Tomar Foto</span>
                                    </button>

                                    {/* Gallery Button */}
                                    <button
                                        onClick={() => fileInputRef.current?.click()}
                                        type="button"
                                        style={{
                                            flex: 1,
                                            height: 100,
                                            background: 'rgba(59, 130, 246, 0.1)',
                                            border: '2px dashed #3b82f6',
                                            borderRadius: 12,
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            gap: 8,
                                            cursor: 'pointer',
                                            color: '#60a5fa',
                                            transition: 'all 0.2s'
                                        }}
                                    >
                                        <ImagePlus size={28} />
                                        <span style={{ fontSize: '0.85rem', fontWeight: 500 }}>Galería</span>
                                    </button>
                                </div>
                            )}
                        </div>

                        {/* Map Section */}
                        <div>
                            <label style={{ display: 'block', marginBottom: 8, color: '#cbd5e1', fontSize: '0.9rem', fontWeight: 500 }}>
                                📍 Ubicación de la Inundación *
                            </label>
                            <p style={{ color: '#64748b', fontSize: '0.8rem', marginBottom: 10, marginTop: 0 }}>
                                Toca el mapa para marcar la ubicación exacta o arrastra el marcador
                            </p>
                            <div style={{ position: 'relative', borderRadius: 12, overflow: 'hidden', border: '2px solid #334155' }}>
                                <MapContainer
                                    center={position || defaultCenter}
                                    zoom={15}
                                    style={{ height: 200, width: '100%' }}
                                    ref={mapRef}
                                >
                                    <TileLayer
                                        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                                        attribution='&copy; OSM'
                                    />
                                    <LocationPicker position={position} setPosition={setPosition} />
                                </MapContainer>
                                <RecenterButton onRecenter={handleRecenter} />
                            </div>
                            {position && (
                                <p style={{ color: '#10b981', fontSize: '0.8rem', marginTop: 8, display: 'flex', alignItems: 'center', gap: 5 }}>
                                    <MapPin size={14} />
                                    Ubicación: {position[0].toFixed(5)}, {position[1].toFixed(5)}
                                </p>
                            )}
                        </div>

                        {/* Water Level Estimate */}
                        <div>
                            <label style={{ display: 'block', marginBottom: 8, color: '#cbd5e1', fontSize: '0.9rem', fontWeight: 500 }}>
                                💧 Nivel de Agua Estimado
                            </label>
                            <div style={{ display: 'flex', gap: 10 }}>
                                {[
                                    { value: 'bajo', label: 'Bajo', color: '#10b981', desc: '< 10cm' },
                                    { value: 'medio', label: 'Medio', color: '#f59e0b', desc: '10-30cm' },
                                    { value: 'alto', label: 'Alto', color: '#ef4444', desc: '> 30cm' }
                                ].map(level => (
                                    <button
                                        key={level.value}
                                        onClick={() => setWaterLevelEstimate(level.value)}
                                        style={{
                                            flex: 1,
                                            padding: '12px 8px',
                                            borderRadius: 10,
                                            border: waterLevelEstimate === level.value
                                                ? `2px solid ${level.color}`
                                                : '2px solid #334155',
                                            background: waterLevelEstimate === level.value
                                                ? `rgba(${level.value === 'bajo' ? '16,185,129' : level.value === 'medio' ? '245,158,11' : '239,68,68'}, 0.15)`
                                                : 'rgba(255,255,255,0.05)',
                                            cursor: 'pointer',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'center',
                                            gap: 4
                                        }}
                                    >
                                        <span style={{
                                            fontWeight: 'bold',
                                            color: waterLevelEstimate === level.value ? level.color : '#94a3b8'
                                        }}>
                                            {level.label}
                                        </span>
                                        <span style={{ fontSize: '0.7rem', color: '#64748b' }}>{level.desc}</span>
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* Description */}
                        <div>
                            <label style={{ display: 'block', marginBottom: 8, color: '#cbd5e1', fontSize: '0.9rem', fontWeight: 500 }}>
                                📝 Descripción (Opcional)
                            </label>
                            <textarea
                                value={description}
                                onChange={(e) => setDescription(e.target.value)}
                                placeholder="Ej: Agua subiendo rápidamente, calle principal bloqueada..."
                                style={{
                                    width: '100%',
                                    minHeight: 80,
                                    padding: 12,
                                    borderRadius: 10,
                                    border: '1px solid #334155',
                                    background: 'rgba(255,255,255,0.05)',
                                    color: '#e2e8f0',
                                    fontSize: '0.9rem',
                                    resize: 'vertical',
                                    fontFamily: 'inherit'
                                }}
                            />
                        </div>

                        {/* Submit Button */}
                        <button
                            onClick={handleSubmit}
                            disabled={isSubmitting || !photo || !position}
                            style={{
                                width: '100%',
                                padding: 16,
                                borderRadius: 12,
                                border: 'none',
                                background: isSubmitting || !photo || !position
                                    ? '#334155'
                                    : 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                                color: isSubmitting || !photo || !position ? '#64748b' : 'white',
                                fontSize: '1rem',
                                fontWeight: 'bold',
                                cursor: isSubmitting || !photo || !position ? 'not-allowed' : 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: 10,
                                boxShadow: isSubmitting || !photo || !position
                                    ? 'none'
                                    : '0 4px 20px rgba(245, 158, 11, 0.4)'
                            }}
                        >
                            {isSubmitting ? (
                                <>
                                    <div className="spinner" style={{
                                        width: 20,
                                        height: 20,
                                        border: '2px solid transparent',
                                        borderTop: '2px solid white',
                                        borderRadius: '50%',
                                        animation: 'spin 1s linear infinite'
                                    }} />
                                    Enviando...
                                </>
                            ) : (
                                <>
                                    <Send size={20} />
                                    Enviar Reporte
                                </>
                            )}
                        </button>

                        <p style={{
                            color: '#64748b',
                            fontSize: '0.75rem',
                            textAlign: 'center',
                            marginTop: 0
                        }}>
                            Tu reporte ayuda a las autoridades y la comunidad a responder más rápido.
                            Mientras instalamos la cámara del sensor, tus reportes manuales son muy valiosos.
                        </p>
                    </div>
                </motion.div>
            </motion.div>

            {/* Spinner Animation */}
            <style>{`
                @keyframes spin {
                    from { transform: rotate(0deg); }
                    to { transform: rotate(360deg); }
                }
            `}</style>
        </AnimatePresence>
    );
}
